import type { DirectiveBinding, ObjectDirective } from 'vue';
import { isVisible } from '@/core/utils/dom';

type Binding = DirectiveBinding<ScrollIntoViewOptions | boolean | undefined, 'immediate'>;

type Storage = {
	/**
	 * Сохраненное состояние видимости
	 */
	isVisible: boolean,

	/**
	 * Текущие настройки скролла
	 */
	scrollIntoViewOptions: ScrollIntoViewOptions | boolean
};

let UtilsDom: typeof import('@/core/utils/dom');
let UtilsLodash: typeof import('@/core/utils/lodash');

/**
 * Сгенерировать настройки для скролла
 *
 * @see https://developer.mozilla.org/ru/docs/Web/API/Element/scrollIntoView
 */
const genScrollIntoViewOptions = (scrollIntoViewOptions?: ScrollIntoViewOptions | boolean) => {
	if (typeof scrollIntoViewOptions === 'boolean') return scrollIntoViewOptions;

	if (!scrollIntoViewOptions) {
		scrollIntoViewOptions = {};
	} else {
		scrollIntoViewOptions = { ...scrollIntoViewOptions };
	}

	scrollIntoViewOptions.behavior ??= 'smooth';
	scrollIntoViewOptions.block ??= 'nearest';
	scrollIntoViewOptions.inline ??= 'nearest';

	return scrollIntoViewOptions;
};

/**
 * Активный элемент, для дедупликации скроллов
 *
 * Только один элемент на странице может считаться активным
 */
let elActive: HTMLElement | undefined;

/**
 * Функция скролла с дедупликацией
 */
let scrollToElActiveDebounce: Function;

/**
 * Выполнить скролл к текущему активному элементу
 */
const scrollToElActive = async () => {
	if (!elActive) return;

	const storage = await useStorage(elActive);
	if (!storage.isVisible) return;

	elActive.scrollIntoView(storage.scrollIntoViewOptions);
};

/**
 * Выполнить дедуплицированный скролл к элементу
 *
 * Будет выполнен скролл к самому последнему элементу, вызвавшему срабатывание директивы
 */
const scrollToElDebounce = async (el: HTMLElement) => {
	if (!UtilsLodash) UtilsLodash = await import('@/core/utils/lodash');
	if (!scrollToElActiveDebounce) scrollToElActiveDebounce = UtilsLodash.debounce(scrollToElActive, 30);

	elActive = el;

	scrollToElActiveDebounce();
};

/**
 * Данные, связанные с элементом и директивой
 */
const useStorage = async (el: HTMLElement, scrollIntoViewOptions?: ScrollIntoViewOptions | boolean) => {
	if (!UtilsDom) UtilsDom = await import('@/core/utils/dom');

	let storage: Storage | undefined = UtilsDom.storage(el, 'v-top-scrollIntoView');

	// инициализация
	if (!storage) {
		storage = {
			isVisible: isVisible(el),
			scrollIntoViewOptions: genScrollIntoViewOptions(scrollIntoViewOptions),
		};

		UtilsDom.storage(el, 'v-top-scrollIntoView', storage);
	}

	return storage;
};

const scrollIntoView: ObjectDirective = {
	async mounted(el: HTMLElement, binding: Binding) {
		await useStorage(el, binding.value);

		if (binding.modifiers.immediate) {
			scrollToElDebounce(el);
		}
	},

	async updated(el: HTMLElement, binding: Binding) {
		const storage = await useStorage(el, binding.value);
		storage.scrollIntoViewOptions = genScrollIntoViewOptions(binding.value);

		const isVisibleNew = isVisible(el);
		if (isVisibleNew && storage.isVisible !== isVisibleNew) {
			scrollToElDebounce(el);
		}

		storage.isVisible = isVisibleNew;
	},
};

export default scrollIntoView;
